import { createContext, useContext, ReactNode, useMemo, useState, useEffect, useCallback } from "react";
import { FormattedFile, useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import { EntityRecord } from "@/types/entity";
import { ISwivuProject } from "@/types/SwivuProject";
import { isImageFileTyped } from "@/react/components/entityViews/files/components/FileHelpers";
import { formatFileMetadata } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { useImageLoading } from "../../../../hooks/useImageLoading";
import { useFileNavigation } from "../hooks/useFileNavigation";
import { MetadataRow } from "@/react/components/entityViews/files/utils/fileMetadataUtils";
import { TComboOption } from "@/modern/components/ui/combobox";
import { useFileSpotIncentive } from "../../../../hooks/useFileSpotIncentive";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { formatFile } from "@/modern/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";

interface FileDetailModalContextValue {
  // File state
  file: FormattedFile | null;
  files: FormattedFile[];
  imageFile: FormattedFile | null;
  imageUrlToLoad: string | undefined;

  // Image loading state
  imageLoading: boolean;
  showSpinner: boolean;
  displayedImageUrl: string | undefined;
  currentImageUrl: string | undefined;
  handleLoadComplete: () => void;

  // Navigation state
  hasPrevious: boolean;
  hasNext: boolean;
  handlePrevious: () => void;
  handleNext: () => void;

  // File permissions
  canEditFile: boolean;
  handleFileDownload: (file: FormattedFile) => void;
  deleteFile: (id: string | number) => void;

  // Spot incentive state
  shouldShowAdjustPoints: (file: FormattedFile | null) => boolean;
  handleAdjustPoints: (file: FormattedFile, isCreatingUnreleasedRecord?: boolean, onReviewClick?: () => void) => Promise<void>;

  // UI state
  activeTab: "review" | "summary";
  setActiveTab: (tab: "review" | "summary") => void;
  summaryTab: "details" | "findings" | "points" | "share";
  setSummaryTab: (tab: "details" | "findings" | "points" | "share") => void;
  isAnnotating: boolean;
  setIsAnnotating: (value: boolean) => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  isAnalyzeDialogOpen: boolean;
  setIsAnalyzeDialogOpen: (open: boolean) => void;

  // Modal state
  open: boolean;
  setOpen: (open: boolean) => void;

  // Computed values
  metadataRows: MetadataRow[];
  shouldShowSpotIncentiveButton: boolean;

  // External dependencies
  entityRecord: EntityRecord;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
  handleFileOpen: (file: FormattedFile) => void;

  // Handlers
  handleAnnotating: () => void;
  handleCreateSpotIncentive: () => void;
  fileChangesVersion: number;
}

const FileDetailModalContext = createContext<FileDetailModalContextValue | undefined>(undefined);

interface FileDetailModalProviderProps {
  children: ReactNode;
  entityRecord: EntityRecord;
  file: FormattedFile | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  project: ISwivuProject | null;
  swivuAppGrants: TComboOption[];
  editorHandler: any;
}

export const FileDetailModalProvider = ({ children, entityRecord, file, open, setOpen, project, swivuAppGrants, editorHandler }: FileDetailModalProviderProps) => {
  const [localFile, setLocalFile] = useState<FormattedFile | null>(file);
  const { files, handleFileOpen, deleteFile, handleFileDownload } = useEntityFileContext();
  const [activeTab, setActiveTab] = useState<"review" | "summary">("review");
  const [summaryTab, setSummaryTab] = useState<"details" | "findings" | "points" | "share">("details");
  const [isAnnotating, setIsAnnotating] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isAnalyzeDialogOpen, setIsAnalyzeDialogOpen] = useState(false);

  // Check if file is an image to determine if we should load it
  const imageFile = useMemo(() => (isImageFileTyped(localFile) ? localFile : null), [localFile]);
  const imageUrlToLoad = imageFile?.originalImageUrl;
  // Compute canEditFile directly
  const canEditFile = Boolean(localFile?.canEdit);

  // Custom hooks for business logic
  const { imageLoading, showSpinner, displayedImageUrl, currentImageUrl, handleLoadComplete } = useImageLoading(imageFile?.id, imageUrlToLoad);
  const { hasPrevious, hasNext, handlePrevious, handleNext } = useFileNavigation(file, files, handleFileOpen);
  const { shouldShowAdjustPoints, handleAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler
  });

  const { subscribe: subscribeToFile, version: fileChangesVersion } = useSwivuStore<BoardFile>({ storeId: STORE_KEYS.boardFilesStore });
  const { user } = useSwivuBryntumContext();

  useEffect(() => {
    return subscribeToFile(({record: changedRecord}) => {
      if (!localFile?.isCCImage && changedRecord.id === localFile?.id) {
        const [f] = formatFile([changedRecord], user);
        setLocalFile(f);
        return true;
      }
      return false;
    });
  }, [subscribeToFile, localFile?.id]);

  useEffect(() => {
    if (file) {
      setLocalFile(file);
    }
  }, [file?.id]);

  // Computed values
  const metadataRows = useMemo(() => formatFileMetadata(localFile), [localFile]);
  const shouldShowSpotIncentiveButton = shouldShowAdjustPoints(localFile);

  // Handlers
  const handleAnnotating = useCallback(() => {
    if (localFile && project && localFile.originalImageUrl) {
      setIsAnnotating(true);
    }
  }, [localFile, project, setOpen]);

  const handleCreateSpotIncentive = useCallback(() => {
    if (localFile) {
      handleAdjustPoints(localFile, true, (newFile?: BoardFile) => {
        if (localFile.isCCImage && newFile) {
          const [f] = formatFile([newFile], user);
          handleFileOpen(f);
        }
        setActiveTab("summary");
        setSummaryTab("points");
        setOpen(true);
      });
    }
  }, [localFile, handleAdjustPoints, setActiveTab, setOpen]);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // Reset tab when modal opens
  useEffect(() => {
    if (open) {
      setActiveTab("review");
    }
  }, [open]);

  // Add keyboard listener for navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        // Don't handle navigation if user is typing in an input field
        const activeElement = document.activeElement;
        const isInputFocused = activeElement && (
          activeElement.tagName === "INPUT" ||
          activeElement.tagName === "TEXTAREA" ||
          activeElement.hasAttribute("contenteditable")
        );

        if (isInputFocused) {
          return; // Let the input handle the arrow keys normally
        }

        e.preventDefault();
        if (e.key === "ArrowLeft") {
          handlePrevious();
        } else {
          handleNext();
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [open, handlePrevious, handleNext]);

  const value: FileDetailModalContextValue = {
    // File state
    file: localFile,
    files,
    imageFile,
    imageUrlToLoad,

    // Image loading state
    imageLoading,
    showSpinner,
    displayedImageUrl,
    currentImageUrl,
    handleLoadComplete,

    // Navigation state
    hasPrevious,
    hasNext,
    handlePrevious,
    handleNext,

    // File permissions
    canEditFile,
    handleFileDownload,
    deleteFile,

    // Spot incentive state
    shouldShowAdjustPoints,
    handleAdjustPoints,

    // UI state
    activeTab,
    setActiveTab,
    summaryTab,
    setSummaryTab,
    isAnnotating,
    setIsAnnotating,
    isFullscreen,
    toggleFullscreen,
    isAnalyzeDialogOpen,
    setIsAnalyzeDialogOpen,

    // Modal state
    open,
    setOpen,

    // Computed values
    metadataRows,
    shouldShowSpotIncentiveButton,

    // External dependencies
    entityRecord,
    project,
    swivuAppGrants,
    editorHandler,
    handleFileOpen,

    // Handlers
    handleAnnotating,
    handleCreateSpotIncentive,

    fileChangesVersion
  };

  return <FileDetailModalContext.Provider value={value}>{children}</FileDetailModalContext.Provider>;
};

export const useFileDetailModal = () => {
  const context = useContext(FileDetailModalContext);
  if (context === undefined) {
    throw new Error("useFileDetailModal must be used within a FileDetailModalProvider");
  }
  return context;
};
